import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Reveal } from "@/components/Reveal";
import { fetchProducts, resolveImg } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

// Pulls the lifestyle photos (model wearing / using the products) from the
// product galleries so they are immediately visible to visitors on the landing page.
// Our lifestyle media ids all carry a "-life" suffix (e.g. prod-power-shirt-life60).
const isLifestyle = (url) => typeof url === "string" && url.includes("-life");

// Ask the Products section (id="products") to open a specific product's detail dialog.
export const openProductDetail = (slug) => {
  if (!slug) return;
  const el = document.getElementById("products");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  window.dispatchEvent(new CustomEvent("ecentra:open-product", { detail: { slug } }));
};

export const LifestyleGallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    fetchProducts()
      .then((products) => {
        if (!active) return;
        const collected = [];
        (products || []).forEach((p) => {
          (p.images || []).forEach((url) => {
            if (isLifestyle(url) && !collected.some((c) => c.src === url)) {
              collected.push({ src: url, caption: p.name, slug: p.slug });
            }
          });
        });
        setItems(collected);
      })
      .catch((err) => {
        console.error("Failed to load lifestyle images:", err);
        setItems([]);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  if (!loading && items.length === 0) return null;

  return (
    <section id="lifestyle" className="scroll-anchor relative overflow-hidden bg-background" data-testid="lifestyle-section">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(900px_circle_at_85%_15%,hsl(168_30%_92%)_0%,transparent_55%)]" />
      <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <Reveal>
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-wider text-accent">Lifestyle</p>
            <h2 className="font-display mt-2 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              Ang Ecentra sa totoong buhay
            </h2>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              Mga tunay na sandali — suot at ginagamit ang Ecentra wellness apparel at Gamat Markisa Plus sa araw-araw.
            </p>
          </div>
        </Reveal>

        {loading ? (
          <div className="mt-10 columns-1 gap-4 sm:columns-2 lg:columns-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="mb-4 h-56 w-full rounded-2xl" />
            ))}
          </div>
        ) : (
          <div className="mt-10 columns-1 gap-4 sm:columns-2 lg:columns-3 [column-fill:_balance]">
            {items.map((item, i) => (
              <Reveal key={item.src} delay={Math.min(i * 0.05, 0.25)}>
                <button
                  type="button"
                  onClick={() => openProductDetail(item.slug)}
                  aria-label={`Tingnan ang ${item.caption}`}
                  className="group relative mb-4 block w-full cursor-pointer break-inside-avoid overflow-hidden rounded-2xl border border-border bg-secondary/40 text-left shadow-[0_10px_30px_-18px_rgba(15,23,42,0.25)] transition-[border-color,box-shadow,transform] duration-200 hover:border-primary/40 hover:shadow-[0_18px_50px_-26px_rgba(15,23,42,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  data-testid={`lifestyle-item-${i}`}
                >
                  <img
                    src={resolveImg(item.src)}
                    alt={`${item.caption} — lifestyle`}
                    loading="lazy"
                    className="w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
                  />
                  <span className="pointer-events-none absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-background/90 px-2.5 py-1 text-xs font-semibold text-primary opacity-0 shadow-sm backdrop-blur-sm transition-opacity duration-200 group-hover:opacity-100">
                    Tingnan <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                  <span className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-foreground/70 via-foreground/20 to-transparent p-4">
                    <span className="text-sm font-semibold text-white drop-shadow-sm">{item.caption}</span>
                    <span className="text-xs font-medium text-white/90 underline-offset-2 group-hover:underline">Shop now</span>
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        )}

        <Reveal>
          <div className="mt-8">
            <Button asChild size="lg" className="rounded-full" data-testid="lifestyle-shop-cta">
              <a href="#products">Shop ang koleksyon</a>
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
